import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, PackageX, RefreshCcw, ShieldCheck, Ban, FileWarning } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtEuro, CLAIM_REASONS } from "@/lib/api";
import { PublicShell } from "@/components/PublicShell";
import { GuaranteeSeal } from "@/components/GuaranteeSeal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HERO = "/img/guarantee-hero.jpg";

const COVERS = [
  { icon: PackageX, t: "Prodotto non ricevuto", d: "Il pacco non è mai arrivato." },
  { icon: FileWarning, t: "Non conforme", d: "Diverso da quanto descritto nella scheda." },
  { icon: Ban, t: "Danneggiato o difettoso", d: "Arrivato rotto o non funzionante." },
  { icon: RefreshCcw, t: "Rimborso mancato", d: "Hai reso il prodotto ma non sei stato rimborsato." },
];

const Steps = ({ s }) => (
  <ol className="grid md:grid-cols-3 gap-5">
    {[
      ["Contatta il negozio", "Spesso basta un messaggio: scrivici prima di aprire la pratica."],
      ["Apri una pratica", `Entro ${s.guarantee_days} giorni dall'ordine, con numero d'ordine ed email d'acquisto.`],
      ["Decisione e rimborso", "Esaminiamo il caso e ti aggiorniamo via email a ogni passaggio."],
    ].map(([t, d], i) => (
      <li key={t} className="bg-white border border-gray-200 rounded-lg p-6">
        <span className="font-display text-5xl font-extrabold text-emerald-900/15">0{i + 1}</span>
        <p className="mt-2 font-display text-lg font-bold tracking-tight">{t}</p>
        <p className="mt-1 text-sm text-gray-600">{d}</p>
      </li>
    ))}
  </ol>
);

const EMPTY = { order_number: "", email: "", name: "", reason: "", amount: "", description: "", contacted_shop: false, website: "" };

const ClaimForm = ({ s }) => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.reason) return toast.error("Seleziona il motivo della richiesta");
    if (!f.contacted_shop) return toast.error("Conferma di aver contattato prima il negozio");
    setSending(true);
    try {
      const { data } = await api.post("/public/claims", { ...f, amount: Number(f.amount) });
      setResult(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (result) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="claim-success">
        <CheckCircle2 size={48} className="mx-auto text-emerald-700" />
        <h3 className="mt-4 font-display text-3xl font-extrabold tracking-tight">Pratica aperta</h3>
        <p className="mt-2 text-gray-600">Numero pratica</p>
        <p className="font-mono text-2xl font-bold text-emerald-900" data-testid="claim-success-number">{result.claim_number}</p>
        <p className="mt-3 text-sm text-gray-600">Importo coperto: {fmtEuro(result.amount)}. Ti abbiamo inviato una conferma via email.</p>
        <Link to="/garanzia/stato" className="mt-6 inline-block text-emerald-900 font-semibold hover:underline" data-testid="claim-success-status-link">Segui lo stato →</Link>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-6 sm:p-8 grid sm:grid-cols-2 gap-5" data-testid="claim-form">
      <div className="space-y-2"><Label>Numero ordine</Label><Input required value={f.order_number} onChange={set("order_number")} placeholder="es. 10234" data-testid="claim-order-input" /></div>
      <div className="space-y-2"><Label>Email usata per l'acquisto</Label><Input required type="email" value={f.email} onChange={set("email")} data-testid="claim-email-input" /></div>
      <div className="space-y-2"><Label>Nome e cognome</Label><Input required minLength={2} value={f.name} onChange={set("name")} data-testid="claim-name-input" /></div>
      <div className="space-y-2">
        <Label>Motivo</Label>
        <Select value={f.reason} onValueChange={(reason) => setF({ ...f, reason })}>
          <SelectTrigger data-testid="claim-reason-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent>
            {Object.entries(CLAIM_REASONS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`claim-reason-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2"><Label>Importo richiesto (€)</Label><Input required type="number" min="0.01" step="0.01" value={f.amount} onChange={set("amount")} data-testid="claim-amount-input" /></div>
      <div className="sm:col-span-2 space-y-2">
        <Label>Descrivi il problema</Label>
        <Textarea required minLength={20} rows={5} value={f.description} onChange={set("description")} placeholder="Cosa è successo? Quando hai contattato il negozio?" data-testid="claim-description-input" />
      </div>
      <label className="sm:col-span-2 flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
        <Checkbox checked={f.contacted_shop} onCheckedChange={(v) => setF({ ...f, contacted_shop: !!v })} data-testid="claim-contacted-checkbox" />
        Confermo di aver già contattato {s.shop_name} senza trovare una soluzione.
      </label>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={f.website} onChange={set("website")} />
      <Button type="submit" disabled={sending} className="sm:col-span-2 justify-self-start rounded-full h-12 px-8 bg-emerald-900 hover:bg-emerald-800" data-testid="claim-submit-button">
        {sending && <Loader2 className="animate-spin mr-2" size={18} />} Apri la pratica
      </Button>
    </form>
  );
};

export default function Guarantee() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/public/summary").then((r) => setS(r.data)); }, []);
  if (!s) return <PublicShell><div className="h-[60vh]" /></PublicShell>;
  return (
    <PublicShell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 fade-up">
          <GuaranteeSeal size="lg" />
          <h1 className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">Compra tranquillo. Se qualcosa va storto, ci pensiamo noi.</h1>
          <p className="mt-5 text-base md:text-lg text-gray-600 max-w-xl">
            La Garanzia Rocard copre i tuoi acquisti fino a <strong className="text-gray-900">{fmtEuro(s.guarantee_max_amount)}</strong> per ordine,
            per <strong className="text-gray-900">{s.guarantee_days} giorni</strong> dalla data dell'ordine. Gratuita e automatica.
          </p>
          <a href="#apri-pratica" className="mt-8 inline-flex rounded-full bg-emerald-900 text-white font-semibold px-7 py-3 hover:bg-emerald-800 transition-colors" data-testid="guarantee-open-claim-cta">Apri una pratica</a>
        </div>
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
          <img src={HERO} alt="Cliente che riceve un pacco" className="rounded-lg border border-gray-200 aspect-[4/5] object-cover w-full" />
          <div className="absolute -bottom-6 -left-6 bg-white border border-gray-200 rounded-lg p-4 shadow-[0_8px_30px_rgba(17,24,39,.08)] flex items-center gap-3">
            <ShieldCheck className="text-emerald-900" /><span className="text-sm font-semibold">Fino a {fmtEuro(s.guarantee_max_amount)} protetti</span>
          </div>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-24">
        <h2 className="font-display text-3xl font-extrabold tracking-tight">Cosa copre</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {COVERS.map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white border border-gray-200 rounded-lg p-6 hover:-translate-y-1 transition-transform duration-200">
              <Icon className="text-emerald-900" /><p className="mt-4 font-semibold">{t}</p><p className="text-sm text-gray-600 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-20">
        <h2 className="font-display text-3xl font-extrabold tracking-tight mb-6">Come funziona</h2>
        <Steps s={s} />
      </section>
      <section id="apri-pratica" className="max-w-3xl mx-auto px-5 sm:px-8 mt-20 scroll-mt-24">
        <h2 className="font-display text-3xl font-extrabold tracking-tight">Apri una pratica di garanzia</h2>
        <p className="mt-2 text-gray-600 mb-6">L'importo coperto non può superare il totale dell'ordine né il massimale di {fmtEuro(s.guarantee_max_amount)}.</p>
        <ClaimForm s={s} />
      </section>
    </PublicShell>
  );
}
